import { CommonModule, formatDate } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  BadgeColor,
  DesignAngularKitModule,
  ItModalComponent,
} from 'design-angular-kit';
import { RequestSummaryComponent } from '../../components/request-summary/request-summary.component';
import { FindRequestDto } from '../../models/find-request.dto';
import {
  RequestCreateDto,
  RequestDto,
  SettingDto,
  SubSettingDto
} from '../../models/request.dto';
import { AuthService } from '../../services/auth.service';
import { PatientService } from '../../services/patient.service';
import { RequestService } from '../../services/request.service';
import { getBadgeType } from '../../models/request.util';
import { Observable, of, switchMap } from 'rxjs';
import { ContextService } from '../../services/context.service';

@Component({
  selector: 'app-request-new',
  standalone: true,
  imports: [
    DesignAngularKitModule,
    FormsModule,
    CommonModule,
    RequestSummaryComponent,
    ReactiveFormsModule,
    RouterModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './request-new.component.html',
  styleUrl: './request-new.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class RequestNewComponent {
  @ViewChild('activeRequestModal') activeRequestModal?: ItModalComponent;
  @ViewChild('rightModal') rightModal!: ItModalComponent;
  @ViewChild('birthAutocomplete', { static: false }) birthAutocomplete!: ElementRef;
  @ViewChild('residentialAutocomplete', { static: false }) residentialAutocomplete!: ElementRef;
  activeRequests: RequestDto[] = [];
  activeTotal: number = 0;
  request: RequestCreateDto | undefined;
  fiscalCode: string | undefined;
  settings: SettingDto[] = [];
  selectedSubsetting: any | undefined;
  selectedSetting: any | undefined;
  subsettingId: string | undefined;
  activeStep: number;
  formPatient!: FormGroup;
  noPatientFound: boolean | undefined;
  _autoCompleteBirthTown: any[] = [];
  birthTowns: any[] = [];
  _autoCompleteResidentialTown: any[] = [];
  residentialTowns: any[] = [];
  errorMessageFiscalCode: boolean = false;
  errorMessage: string = '';
  private autocompleteObserver!: MutationObserver;

  constructor(
    private patientService: PatientService,
    public authService: AuthService,
    private requestService: RequestService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private context: ContextService
  ) {
    this.activeStep = 0;
  }

  ngOnInit(): void {
    if(this.context.patientFiscalCode) {
      this.fiscalCode = this.context.patientFiscalCode;
      this.findPatient();
    }
    this.getSettings();
    this.noPatientFound = false;
    this.formPatient = this.fb.group({
      name: ['', Validators.required],
      surname: ['', Validators.required],
      birthDate: ['', Validators.required],
      birthTown: ['', Validators.required],
      fiscalCode: [
        '',
        [
          Validators.minLength(16),
          Validators.maxLength(16),
          Validators.pattern('^[A-Za-z]{6}[0-9]{2}[A-Za-z]{1}[0-9]{2}[A-Za-z]{1}[0-9A-Za-z]{3}[A-Za-z]{1}$'),
          Validators.required,
        ],
      ],
      emailAddress: [''],
      residentialTown: ['', Validators.required],
      residentialAddress: ['', Validators.required],
      sex: ['', Validators.required],
      isCoherent: []
    });
    const currentDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    this.getTowns(currentDate);
  }

  ngAfterViewInit() {
    this.observeAutocomplete();
  }

  observeAutocomplete() {
  const host = this.birthAutocomplete?.nativeElement;
  const hostRes = this.residentialAutocomplete?.nativeElement;

  if (!host || ! hostRes) return;

  this.autocompleteObserver = new MutationObserver(() => {
    const input: HTMLInputElement | null = host.querySelector('input.autocomplete__input');
    const inputRes: HTMLInputElement | null = hostRes.querySelector('input.autocomplete__input');

    if (input) {
      if (this.formPatient.disabled) {
        input.disabled = true;
      } else {
        input.disabled = false;
      }
    }
    if (inputRes) {
      if (this.formPatient.disabled) {
        inputRes.disabled = true;
      } else {
        inputRes.disabled = false;
      }
    }
  });

  this.autocompleteObserver.observe(host, {
    childList: true,
    subtree: true,
  });
}

  getTowns(date: string) {
    const list: any[] = [];
    this.patientService.findTown(date).subscribe( result => {
      if (result) {
          for (const item of result) {
            list.push(item);
          }
          this._autoCompleteResidentialTown = list.map(item => (
            item.description
        ));
        this.residentialTowns = list;
        }
    });
  }

  loadRequests(): void {
    const request: FindRequestDto = {
      page: 0,
      size: 100,
      patientId: this.request?.patient.id,
      statusCodes: ['PIC', 'NEW', 'CP', 'CS','DAT','TPV','VAL','CPZ','CST','BOZ','APE','TPA'],
    };
    this.requestService.getRequests(request).subscribe((res) => {
      this.activeRequests = res.transitions;
      this.activeTotal = res.total;
      if (this.activeRequests?.length) {
        this.activeRequestModal?.toggle();
      }
    });
  }

  autocompleteBirthTown$ = (
    search: string | null | undefined = ''
  ): Observable<Array<string>> => {
    if (!search) {
      return of([]);
    }
    return of([]);
  };

  getSettings() {
    this.requestService
      .getSettings(this.authService.userInfo.username)
      .subscribe((response) => {
        if (response) {
          this.settings = response as SettingDto[];
        }
      });
  }

  getBirthTown() {
    if(this.formPatient.controls['birthDate'].value) {
      this.getTownsWithDate(this.formPatient.controls['birthDate'].value);
    }
  }

  getTownsWithDate(date: Date) {
    const list: any[] = [];
    this.patientService.findTown(date).subscribe( result => {
      if (result) {
          for (const item of result) {
            list.push(item);
          }
          this._autoCompleteBirthTown = list.map(item => (
            item.description
        ));
        this.birthTowns = list;
        }
    });
  }

  changeBirthTown(event: any) {
    this.formPatient.controls['birthTown'].setValue(this.birthTowns.find(item => item.description === event)) ;
  }

  source = (query: string, populateResults: (results: any[]) => void) => {
    const results = this._autoCompleteBirthTown;
    const filteredResults = results.filter(result => result.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    populateResults(filteredResults);
  };

  getSubsettings(): SubSettingDto[] {
    return (
      this.settings.find((setting) => setting.code === this.selectedSetting)
        ?.subSettings || []
    );
  }

  onSelectedSetting(event: any) {
    // if (this.getSubsettings()?.length === 1) {
    //   this.selectedSubsetting = this.getSubsettings()[0].id;
    // }
    this.selectedSubsetting = this.getSubsettings()[this.getSubsettings().findIndex(sub => !sub.visible)].id;

  }

  findPatient() {
    this.noPatientFound = false;
    const payload = {
      anagraficaAssistito: {
        codiceFiscale: this.fiscalCode!.toUpperCase(),
      },
      anagraficaApplicativo: {
        codFiscOperatore: '',
        codiceApplicativo: 'YEFARYWRCX',
        nomeApplicativo: 'we-com',
      },
    };
    this.patientService.findPatient(payload).subscribe((response) => {
      if (response && response?.patient) {
        this.noPatientFound = false;
        this.request = { patient: response?.patient };
      } else {
        this.noPatientFound = true;
        this.formPatient.controls['fiscalCode'].setValue(this.fiscalCode);
      }
    });
  }

  refreshPatient() {
    this.request = undefined;
    this.fiscalCode = undefined;
    this.activeStep = 0;
  }

  savePatient() {
    this.patientService.checkFiscalCode(this.formPatient.value).subscribe(result => {
      if(result?.result) {
        this.formPatient.controls['isCoherent'].setValue(true);
      this.patientService
      .newPatient(this.formPatient.value)
      .subscribe((response) => {
        if (response) {
          this.request = { patient: response.patient };
          this.noPatientFound = false;
          console.log(this.rightModal)
          this.rightModal.toggle();
        } else {
          //inserire alert paziene non inserito e anche errore
        }
      });
      } else {
        this.errorMessageFiscalCode = true;
        this.formPatient.disable();
        this.observeAutocomplete();
      }
      
    }); 
    
  }

  calculateFiscalCode() {
    this.patientService.calculateFiscalCode(this.formPatient.value).subscribe(result => {
      console.log(result)
      if(result?.calculatedFiscalCode) {
        if(result?.calculatedFiscalCode != this.formPatient.controls['fiscalCode'].value) {
          this.errorMessage = 'Il codice fiscale calcolato è diverso da quello precedentemente inserito.'
        }
        this.formPatient.controls['fiscalCode'].setValue(result.calculatedFiscalCode);
      }
    });
  }

  savePatientDirect() {
    this.formPatient.controls['isCoherent'].setValue(false);
    this.patientService
      .newPatient(this.formPatient.value)
      .subscribe((response) => {
        if (response) {
          this.request = { patient: response.patient };
          this.noPatientFound = false;
          this.rightModal.toggle();
        } else {
          //inserire alert paziene non inserito e anche errore
        }
      });
  }

  modifyPatient() {
    this.errorMessageFiscalCode = false;
    this.formPatient.enable();
    this.observeAutocomplete();
  }

  async goOn() {
    this.activeStep = this.activeStep + 1;
    if (this.activeStep === 1) {
      this.loadRequests();
    }
    if (this.activeStep === 2) {
      const request = {
        patientId: this.request?.patient.id,
        subsettingId: this.selectedSubsetting,
      };
      this.requestService.createTransition(request).subscribe((response) => {
        if (response && response.id) {
          this.router.navigate([`/detail/${response.uuid}`], {
            state: { openFormIndex: 0 },
          });
        }
      });
    }
  }

  goBack() {
    this.activeStep = this.activeStep - 1;
  }

  getBadgeColor(code: string): BadgeColor {
    return getBadgeType(code);
  }
}
